import {
  Directive,
  ElementRef,
  HostListener,
  inject,
  input,
  OnDestroy,
  Renderer2,
  signal,
  effect,
} from '@angular/core';
import type { FieldTree } from '@angular/forms/signals';
import { FormErrorService } from '@aurrica-web/core-data';
import { getFieldStateForKey } from '../utils/field-tree';

/**
 * Configuration object linking the directive to a Signal Form field.
 */
export type SignalFormTooltipInput = {
  /** The root object of the signal-based form. */
  root: FieldTree<unknown>;
  /** The specific control name or path within the form tree. */
  control: string;
};

/**
 * A reactive error tooltip directive specifically designed for Signal-based forms.
 *
 * This directive monitors the validation state of a form field in real-time.
 * If validation errors exist, it displays them in a high-contrast tooltip when
 * the user hovers over the host element.
 *
 * @example
 * ```html
 * <input
 *    [libUiErrorTooltip]="{ root: form.root, control: 'email' }"
 *    [placement]="'right'"
 * />
 *

```

*/
@Directive({
  selector: '[libUiErrorTooltip]',
  standalone: true,
})
export class ErrorTooltipDirective implements OnDestroy {
  /**

* The required signal form configuration.
* Property name matches the selector to allow direct binding without an alias.
*/
  libUiErrorTooltip = input.required<SignalFormTooltipInput>();

  /*

* The placement of the tooltip relative to the host element.
* @defaultValue 'top'
*/
  placement = input<'top' | 'bottom' | 'left' | 'right'>('top');

  /* @internal Reference to the host element for positioning calculations. */
  private readonly el = inject(ElementRef);

  /* @internal Service to convert technical validation keys into human-readable messages. */
  private readonly formErrors = inject(FormErrorService);

  /* @internal Renderer for safe, cross-platform DOM manipulation. */
  private readonly renderer = inject(Renderer2);

  /* @internal Reference to the dynamically created tooltip element. */
  private tooltipEl: HTMLElement | null = null;

  /* @internal Internal signal storing the current list of error strings. */
  private readonly errors = signal<string[]>([]);

  constructor() {
    /*
     * Reactive synchronization:
     * Listens to changes in the form field state and updates the internal
     * error list. If the tooltip is currently visible, it triggers a content refresh.
     */
    effect(() => {
      const config = this.libUiErrorTooltip();
      const { root, control } = config;

      const state = getFieldStateForKey(root, control);
      if (!state) {
        this.errors.set([]);
        return;
      }

      const msgs = this.formErrors.messagesFromValidationErrors(state.errors());
      this.errors.set(msgs);

      if (this.tooltipEl) {
        this.updateTooltipContent();
      }
    });
  }

  /*
   * Host listener to trigger tooltip visibility.
   * Only instantiates the tooltip if active errors exist.
   */
  @HostListener('mouseenter')
  onHover(): void {
    if (this.errors().length > 0) {
      this.show();
    }
  }

  /*

* Host listener to initiate tooltip dismissal.
*/
  @HostListener('mouseleave')
  onLeave(): void {
    this.hide();
  }

  /*

* Creates the tooltip element with cinematic high-contrast styling and appends it to the body.
*/
  private show(): void {
    if (this.tooltipEl) return;

    this.tooltipEl = this.renderer.createElement('div');
    const classes = [
      'fixed',
      'z-[10000]',
      'max-w-xs',
      'bg-red-100',
      'text-red-700',
      'dark:bg-red-900',
      'dark:text-red-100',
      'text-xs',
      'font-medium',
      'rounded-md',
      'px-3',
      'py-2',
      'shadow-lg',
      'border',
      'border-red-200',
      'dark:border-red-800',
      'whitespace-normal',
      'pointer-events-none',
      'transition-opacity',
      'duration-200',
      'opacity-0',
    ];

    classes.forEach((cls) => this.renderer.addClass(this.tooltipEl, cls));
    this.updateTooltipContent();
    this.renderer.appendChild(document.body, this.tooltipEl);

    requestAnimationFrame(() => {
      this.positionTooltip();
      if (this.tooltipEl) {
        this.renderer.setStyle(this.tooltipEl, 'opacity', '1');
      }
    });
  }

  /*

* Dynamically builds a list of errors inside the tooltip container.
*/
  private updateTooltipContent(): void {
    if (!this.tooltipEl) return;

    this.renderer.setProperty(this.tooltipEl, 'innerHTML', '');

    const ul = this.renderer.createElement('ul');
    this.renderer.addClass(ul, 'list-disc');
    this.renderer.addClass(ul, 'list-inside');
    this.renderer.addClass(ul, 'space-y-0.5');

    this.errors().forEach((msg) => {
      const li = this.renderer.createElement('li');
      this.renderer.setProperty(li, 'textContent', msg);
      this.renderer.appendChild(ul, li);
    });

    this.renderer.appendChild(this.tooltipEl, ul);
  }

  /*

* Fades out the tooltip and schedules its removal from the DOM.
*/
  private hide(): void {
    if (this.tooltipEl) {
      this.renderer.setStyle(this.tooltipEl, 'opacity', '0');
      setTimeout(() => this.destroyTooltip(), 200);
    }
  }

  /*
   *
   * Calculates viewport coordinates based on the host element's position and current placement.
   */
  private positionTooltip(): void {
    if (!this.tooltipEl) return;

    const hostRect = this.el.nativeElement.getBoundingClientRect();
    const tooltipRect = this.tooltipEl.getBoundingClientRect();
    const offset = 8;

    let top = 0;
    let left = 0;

    switch (this.placement()) {
      case 'top':
        top = hostRect.top - tooltipRect.height - offset;
        left = hostRect.left + hostRect.width / 2 - tooltipRect.width / 2;
        break;
      case 'bottom':
        top = hostRect.bottom + offset;
        left = hostRect.left + hostRect.width / 2 - tooltipRect.width / 2;
        break;
      case 'left':
        top = hostRect.top + hostRect.height / 2 - tooltipRect.height / 2;
        left = hostRect.left - tooltipRect.width - offset;
        break;
      case 'right':
        top = hostRect.top + hostRect.height / 2 - tooltipRect.height / 2;
        left = hostRect.right + offset;
        break;
    }

    this.renderer.setStyle(this.tooltipEl, 'top', `${top}px`);
    this.renderer.setStyle(this.tooltipEl, 'left', `${left}px`);
  }

  /*

* Removes the tooltip from the body and nullifies the reference.
*/
  private destroyTooltip(): void {
    if (this.tooltipEl) {
      this.renderer.removeChild(document.body, this.tooltipEl);
      this.tooltipEl = null;
    }
  }

  /* @internal Clean up DOM elements on directive destruction. */
  ngOnDestroy(): void {
    this.destroyTooltip();
  }
}
